import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Star, Quote, BadgeCheck } from 'lucide-react';
import { SectionTitle, FlowerMotif } from './premium/Decor';

/**
 * Customer testimonials with premium AI-generated Indian portraits stored
 * locally in /public/generated/testimonials/ (each ~20KB, WebP, mobile-fast).
 */
const TESTIMONIALS = [
  {
    name: 'Kajal Sharma',
    nameHi: 'काजल शर्मा',
    city: 'Mumbai',
    cityHi: 'मुंबई',
    rating: 5,
    photo: '/generated/testimonials/kajal-mumbai.webp',
    hi: 'सच में मुझे लगा था ये भी एक generic रिपोर्ट होगी, but this was very different. जिस तरह से इसने मेरे personality, decision patterns और relationship struggles को explain किया — ऐसा लगा किसी ने सच में मेरी ज़िंदगी समझी हो। उपाय भी simple और doable थे।',
    en: "Honestly, mujhe laga tha ye bhi ek generic report hogi, but this was very different. The way it explained my personality, decision patterns, and even relationship struggles — it felt like someone really understood my life. The remedies were simple and doable.",
  },
  {
    name: 'Ankita Verma',
    nameHi: 'अंकिता वर्मा',
    city: 'Delhi',
    cityHi: 'दिल्ली',
    rating: 5,
    photo: '/generated/testimonials/ankita-delhi.webp',
    hi: 'जो मुझे सबसे ज़्यादा पसंद आया वो था depth — planetary placements से लेकर life patterns और future timelines तक, सब कुछ clearly connected था। महीनों से जिन decisions पर अटकी थी, उन पर finally clarity मिल गई। बहुत-बहुत धन्यवाद!',
    en: "What stood out for me was the depth. From planetary placements to life patterns and future timelines, everything was clearly connected. I finally got clarity on a few major decisions I was stuck on for months. Thank you so much!",
  },
  {
    name: 'Nitya Patel',
    nameHi: 'नित्या पटेल',
    city: 'Ahmedabad',
    cityHi: 'अहमदाबाद',
    rating: 5,
    photo: '/generated/testimonials/nitya-ahmedabad.webp',
    hi: 'इतने apps try किए… कुछ काम नहीं आया। 6 महीने तक job apply करने के बाद मिल भी गई! बताए गए उपाय वाकई काम कर गए। Hats off Karma Kundali team को!',
    en: "Tried so many apps… nothing worked. Got the job after 6 months of applying! The remedies actually worked. Hats off to Karma Kundali team!",
  },
  {
    name: 'Deepak Joshi',
    nameHi: 'दीपक जोशी',
    city: 'Pune',
    cityHi: 'पुणे',
    rating: 5,
    photo: '/generated/testimonials/deepak-pune.webp',
    hi: 'पहले मैंने कई कुंडली रिपोर्ट्स पढ़ी हैं, but इस वाली ने actually explain किया कि मेरी ज़िंदगी में चीज़ें क्यों हो रही थीं। मेरे career phases और दशा timing पर जो insights थे — surprisingly accurate। ये report कम और roadmap ज़्यादा लगी।',
    en: "I've gone through multiple Kundli reports before, but this one actually explained why things were happening in my life. The insights on my career phases and dasha timing were surprisingly accurate. It felt less like a report and more like a roadmap.",
  },
  {
    name: 'Priya Kapoor',
    nameHi: 'प्रिया कपूर',
    city: 'Jaipur',
    cityHi: 'जयपुर',
    rating: 5,
    photo: '/generated/testimonials/priya-jaipur.webp',
    hi: 'विवाह योग और सही समय बिल्कुल वैसा ही निकला जैसा कुंडली में लिखा था। न्यूमेरोलॉजी रिपोर्ट तो free बोनस में मिली — पैसा वसूल! परिवार के सबको recommend कर दिया है।',
    en: "The marriage timing prediction was spot-on. The numerology report came as a free bonus — incredible value! I've recommended this to everyone in my family.",
  },
  {
    name: 'Rohit Iyer',
    nameHi: 'रोहित अय्यर',
    city: 'Bangalore',
    cityHi: 'बैंगलोर',
    rating: 5,
    photo: '/generated/testimonials/rohit-bangalore.webp',
    hi: 'शुरू में skeptical था, लेकिन business में जो remedies बताए — मंगल के मंत्र और रत्न — उन्होंने 4 महीने में ही टर्नअराउंड दिखा दिया। ज्योतिष पर अब विश्वास हो गया।',
    en: "I was skeptical at first, but the business remedies — Mangal mantras and gemstone — showed a clear turnaround within 4 months. I now genuinely believe in astrology.",
  },
];

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="testimonials-section"
      className="relative bg-brand-cream py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-50 pointer-events-none" />
      <div className="absolute top-1/2 -right-48 w-[480px] h-[480px] bg-mandala-large bg-no-repeat bg-center opacity-30 pointer-events-none -translate-y-1/2" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'हमारे खुश ग्राहक' : 'Our Happy Customers'}
          title={isHi ? '10,000+ लोगों ने पाया सही मार्गदर्शन' : 'We\'ve Helped Thousands Find Their Path'}
          subtitle={isHi
            ? 'पढ़िए कैसे हमारी कुंडली रिपोर्ट ने उनके जीवन में बदलाव लाया।'
            : 'Read how our Kundli reports transformed their lives.'}
          language={language}
        />

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="relative bg-white rounded-3xl border border-brand-gold/25 p-6 md:p-7 shadow-sm hover:shadow-gold-glow hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <Quote className="absolute top-5 right-5 w-9 h-9 text-brand-gold/20" />

              {/* Stars + verified badge */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-brand-gold-dark text-brand-gold-dark" />
                  ))}
                </div>
                <div className="flex items-center gap-1 ml-1.5 text-green-700">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${isHi ? 'font-hindi' : ''}`}>
                    {isHi ? 'सत्यापित' : 'Verified'}
                  </span>
                </div>
              </div>

              {/* Quote */}
              <p className={`text-brand-text-soft text-sm md:text-base leading-relaxed mb-6 flex-1 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                "{isHi ? t.hi : t.en}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-5 border-t border-brand-gold/15">
                <div className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-brand-gold/40 flex-shrink-0">
                  <img
                    src={t.photo}
                    alt={t.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // fallback to initial avatar if photo fails
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <p className={`font-bold text-brand-text-dark text-sm ${isHi ? 'font-hindi' : ''}`}>
                    {isHi ? t.nameHi : t.name}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-brand-text-mute">
                    <FlowerMotif className="w-3 h-3 text-brand-gold-dark" />
                    <span className={isHi ? 'font-hindi' : ''}>{isHi ? t.cityHi : t.city}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {[
            { num: '10K+', hi: 'खुश ग्राहक', en: 'Happy Clients' },
            { num: '4.9/5', hi: 'औसत रेटिंग', en: 'Avg. Rating' },
            { num: '15+', hi: 'वर्ष अनुभव', en: 'Years Experience' },
            { num: '99%', hi: 'सटीकता', en: 'Accuracy' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-playfair font-black text-3xl md:text-4xl text-brand-maroon">{s.num}</div>
              <div className={`text-xs uppercase tracking-wider text-brand-gold-dark mt-1 font-semibold ${isHi ? 'font-hindi' : ''}`}>
                {isHi ? s.hi : s.en}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
